import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import type { CrmDataProvider } from "../providers/types";
import type {
  BitrixReport,
  BitrixStatus,
  CrmSource,
} from "../providers/vv/dataProvider";
import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../root/ConfigurationContext";

const BITRIX_STATUS_KEY = ["bitrix-status"];
const ENTITIES = [
  "projects",
  "stages",
  "companies",
  "contacts",
  "leads",
  "deals",
  "notes",
  "tasks",
] as const;
const ARCHIVED = ["companies", "contacts", "leads", "deals"] as const;

const errorMessage = (error: unknown) =>
  (error as { body?: { message?: string } })?.body?.message ??
  (error as Error)?.message ??
  String(error);

const dateTime = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

/**
 * Подключения → Битрикс24, below the webhook field (owner only): the mode
 * (own CRM or Bitrix24 as the source), «Проверить», «Пробный перенос»,
 * «Перенести» (both run in crm-api in the background; the status is polled
 * while one runs) and the last report. crm-api checks everything again.
 */
export const BitrixPanel = ({ hasWebhook }: { hasWebhook: boolean }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const config = useConfigurationContext();
  const updateConfiguration = useConfigurationUpdater();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [check, setCheck] = useState<{ ok: boolean; text: string } | null>(
    null,
  );
  const { data: status } = useQuery<BitrixStatus>({
    queryKey: BITRIX_STATUS_KEY,
    queryFn: () => dataProvider.getBitrixStatus(),
    refetchInterval: (query) => (query.state.data?.running ? 2000 : false),
  });
  const mode: CrmSource =
    status?.crmSource ?? (config.crmSource === "bitrix" ? "bitrix" : "own");
  const running = status?.running ?? null;

  const fail = (error: unknown) =>
    notify(errorMessage(error), {
      type: "error",
      messageArgs: { _: errorMessage(error) },
    });

  const setMode = async (next: CrmSource) => {
    setBusy(true);
    try {
      const { crmSource } = await dataProvider.saveBitrixMode(next);
      // the whole app follows at once: marks, hidden buttons
      updateConfiguration({ ...config, crmSource });
      await queryClient.invalidateQueries({ queryKey: ["configuration"] });
      await queryClient.invalidateQueries({ queryKey: BITRIX_STATUS_KEY });
      notify("crm.connections.saved", { type: "success" });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setCheck(null);
    try {
      const { name } = await dataProvider.testBitrix();
      setCheck({
        ok: true,
        text: name
          ? translate("crm.bitrix.check_ok", { name })
          : translate("crm.bitrix.check_ok_empty"),
      });
    } catch (error) {
      setCheck({ ok: false, text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  const start = async (kind: "dry" | "import") => {
    setBusy(true);
    try {
      await dataProvider.runBitrix(kind);
      notify(kind === "dry" ? "crm.bitrix.dry_started" : "crm.bitrix.started", {
        type: "info",
      });
      await queryClient.invalidateQueries({ queryKey: BITRIX_STATUS_KEY });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <div className="flex flex-col gap-1 border-t pt-4 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">{translate("crm.bitrix.mode")}</span>
          <Switch
            checked={mode === "bitrix"}
            disabled={busy || status === undefined}
            onCheckedChange={(on) => void setMode(on ? "bitrix" : "own")}
            aria-label={translate("crm.bitrix.mode")}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {translate(
            mode === "bitrix" ? "crm.bitrix.mode_on" : "crm.bitrix.mode_off",
          )}
        </p>
      </div>

      <div className="flex flex-col gap-2 border-t pt-4 min-w-0">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || !hasWebhook}
            onClick={() => void test()}
          >
            {translate("crm.bitrix.check")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || !hasWebhook || !!running}
            onClick={() => void start("dry")}
          >
            {translate("crm.bitrix.dry_run")}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={busy || !hasWebhook || !!running}
            onClick={() => setConfirmOpen(true)}
          >
            {translate("crm.bitrix.run")}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {translate(
            hasWebhook ? "crm.bitrix.run_hint" : "crm.bitrix.no_webhook",
          )}
        </p>
        {check ? (
          <p
            role="status"
            data-testid="bitrix-check"
            className={cn(
              "text-sm break-words",
              check.ok ? "text-foreground" : "text-destructive",
            )}
          >
            {check.text}
          </p>
        ) : null}
      </div>

      <BitrixState status={status} />

      <Confirm
        isOpen={confirmOpen}
        title="crm.bitrix.run_confirm_title"
        content="crm.bitrix.run_confirm"
        confirm="crm.bitrix.run"
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          void start("import");
        }}
      />
    </div>
  );
};

const BitrixState = ({ status }: { status?: BitrixStatus }) => {
  const translate = useTranslate();
  if (!status) return null;
  const { running, lastError, lastReport, lastPulledAt } = status;
  if (!running && !lastError && !lastReport && !lastPulledAt) return null;
  return (
    <div
      className="flex flex-col gap-2 border-t pt-4 min-w-0"
      data-testid="bitrix-state"
    >
      {running ? (
        <p role="status" className="text-sm">
          {translate(`crm.bitrix.running.${running}`, {
            _: translate("crm.bitrix.running.import"),
          })}
        </p>
      ) : null}
      {lastPulledAt ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.bitrix.last_pulled", {
            time: dateTime(lastPulledAt),
          })}
        </p>
      ) : null}
      {lastError ? (
        <p className="text-sm text-destructive break-words">{lastError}</p>
      ) : null}
      {lastReport ? <BitrixReportView report={lastReport} /> : null}
    </div>
  );
};

/** created / updated / skipped by kind of record, archived, employees, what to look at */
export const BitrixReportView = ({ report }: { report: BitrixReport }) => {
  const translate = useTranslate();
  const [all, setAll] = useState(false);
  const lines = report.lines ?? [];
  const shown = all ? lines : lines.slice(0, 8);
  const archived = ARCHIVED.filter((a) => (report.archived?.[a] ?? 0) > 0);
  const unmatched = report.users?.unmatched ?? [];
  return (
    <div className="flex flex-col gap-3 min-w-0" data-testid="bitrix-report">
      <p className="text-sm font-medium">
        {translate(
          report.dry
            ? "crm.bitrix.report_dry"
            : report.kind === "sync"
              ? "crm.bitrix.report_sync"
              : "crm.bitrix.report_import",
          { time: dateTime(report.finishedAt) },
        )}
      </p>
      <table className="w-full text-sm tabular-nums">
        <thead>
          <tr className="text-xs text-muted-foreground">
            <th className="text-left font-normal py-1" />
            <th className="text-right font-normal py-1 px-1">
              {translate("crm.bitrix.created")}
            </th>
            <th className="text-right font-normal py-1 px-1">
              {translate("crm.bitrix.updated")}
            </th>
            <th className="text-right font-normal py-1 pl-1">
              {translate("crm.bitrix.skipped")}
            </th>
          </tr>
        </thead>
        <tbody>
          {ENTITIES.map((e) => {
            const c = report.counts?.[e];
            if (!c) return null;
            return (
              <tr key={e} className="border-t">
                <td className="py-1">
                  {translate(`crm.bitrix.entities.${e}`)}
                </td>
                <td className="text-right py-1 px-1">{c.created}</td>
                <td className="text-right py-1 px-1">{c.updated}</td>
                <td className="text-right py-1 pl-1">{c.skipped}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {archived.length ? (
        <p className="text-sm">
          {translate("crm.bitrix.archived")}{" "}
          {archived
            .map(
              (a) =>
                `${translate(`crm.bitrix.entities.${a}`)}: ${report.archived[a]}`,
            )
            .join(", ")}
        </p>
      ) : null}
      <p className="text-sm">
        {translate("crm.bitrix.users_matched", {
          count: report.users?.matched ?? 0,
        })}
      </p>
      {unmatched.length ? (
        <div className="flex flex-col gap-1 min-w-0">
          <p className="text-sm">{translate("crm.bitrix.users_unmatched")}</p>
          <ul className="text-sm list-disc pl-5 break-words">
            {unmatched.map((u) => (
              <li key={u.id}>
                {u.name}
                {u.email ? (
                  <span className="text-muted-foreground"> {u.email}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {lines.length ? (
        <div className="flex flex-col gap-1 min-w-0">
          <p className="text-sm">{translate("crm.bitrix.lines")}</p>
          <ul className="text-sm list-disc pl-5 break-words text-muted-foreground">
            {shown.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
          {lines.length > shown.length || all ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start"
              onClick={() => setAll(!all)}
            >
              {translate(
                all ? "crm.bitrix.lines_less" : "crm.bitrix.lines_all",
                {
                  count: lines.length,
                },
              )}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
